// ============================================================
//  MASAMADREAM — Activities Card
//  Fase 3+: checkbox = toggle, row click = open instructions modal
// ============================================================

import { PROCESSES }       from './data.js';
import { toast }           from './toast.js';
import { openActivityModal } from './activity-modal.js';

/**
 * @param {object}   process
 * @param {Store}    store
 * @param {Function} onDayComplete  - (dayNum, totalDays) callback
 */
export function renderActivities(process, store, onDayComplete) {
  const container = document.getElementById('mm-activities-body');
  if (!container) return;

  const pData = PROCESSES[process.type];
  if (!pData) return;

  // Re-fetch the process from store to get fresh data
  const freshProcess = store.getProcess(process.id);
  if (!freshProcess) return;
  process = freshProcess;

  // Group by day
  const byDay = {};
  pData.activities.forEach(act => {
    (byDay[act.day] = byDay[act.day] || []).push(act);
  });

  container.innerHTML = '';

  Object.entries(byDay).forEach(([day, acts]) => {
    const dayNum   = parseInt(day, 10);
    const isToday  = dayNum === process.currentDay;
    const isPast   = dayNum <  process.currentDay;
    const isFuture = dayNum >  process.currentDay;

    const doneCount  = acts.filter(a => store.isCompleted(a.id, process.id)).length;
    const totalCount = acts.length;
    const dayDone    = doneCount === totalCount;

    // ── Day group ─────────────────────────────────────────
    const group = document.createElement('div');
    group.className = 'mm-activity-day';

    // ── Day header ────────────────────────────────────────
    const header = document.createElement('div');
    header.className = 'mm-activity-day-header';

    const lbl = document.createElement('span');
    lbl.className = 'mm-activity-day-label';
    lbl.textContent = `Día ${day}`;
    if (isToday && !dayDone) lbl.style.color = 'var(--mm-blue)';
    if (dayDone)             lbl.style.color = 'var(--mm-green)';

    const badge = document.createElement('span');
    badge.className = `mm-activity-badge ${dayDone ? 'is-done' : isToday ? 'is-today' : ''}`;
    badge.textContent = `${doneCount}/${totalCount}`;

    header.appendChild(lbl);
    header.appendChild(badge);
    group.appendChild(header);

    // ── Activity rows ─────────────────────────────────────
    acts.forEach(act => {
      const done     = store.isCompleted(act.id, process.id);
      const tappable = isToday || isPast;

      const item = document.createElement('div');
      item.className = [
        'mm-activity-item',
        done             ? 'is-done'   : '',
        isToday && !done ? 'is-active' : '',
        isFuture         ? 'is-future' : '',
      ].filter(Boolean).join(' ');
      item.setAttribute('role', 'button');
      item.setAttribute('tabindex', tappable ? '0' : '-1');
      item.setAttribute('aria-label', `${act.action} — ver instrucciones`);

      // Checkbox
      const box = document.createElement('div');
      box.className = ['mm-checkbox',
        done             ? 'is-done'   : '',
        isToday && !done ? 'is-active' : '',
        isFuture         ? 'is-future' : '',
      ].filter(Boolean).join(' ');
      box.setAttribute('role', 'checkbox');
      box.setAttribute('aria-checked', done ? 'true' : 'false');
      box.setAttribute('aria-label', `Marcar ${act.action}`);
      box.setAttribute('tabindex', '-1'); // handled by parent
      if (done) box.textContent = '✓';

      // Info
      const info = document.createElement('div');
      info.className = 'mm-activity-info';

      const name = document.createElement('div');
      name.className = 'mm-activity-name';
      name.textContent = `${act.emoji || ''} ${act.action}`.trim();

      const desc = document.createElement('div');
      desc.className = 'mm-activity-desc';
      desc.textContent = act.duration;

      info.appendChild(name);
      info.appendChild(desc);

      // Chevron (indicates modal opens on tap)
      const chevron = document.createElement('span');
      chevron.className = 'mm-activity-chevron';
      chevron.setAttribute('aria-hidden', 'true');
      chevron.textContent = '›';

      item.appendChild(box);
      item.appendChild(info);
      if (!isFuture) item.appendChild(chevron);
      group.appendChild(item);

      // ── Event handlers ───────────────────────────────────
      if (tappable) {
        // Checkbox click: toggle completion (stop propagation so row doesn't also fire)
        box.addEventListener('click', (e) => {
          e.stopPropagation();
          _toggleActivity(act, done, store, acts, isToday, process, onDayComplete);
        });

        // Row click: open modal with instructions
        item.addEventListener('click', () => {
          openActivityModal(act, store.isCompleted(act.id, process.id), (a) => {
            // Called from modal "Marcar completo / Desmarcar" button
            // Toggle: if currently done, uncomplete; if not done, complete
            const currentlyDone = store.isCompleted(a.id, process.id);
            _toggleActivity(a, currentlyDone, store, acts, isToday, process, onDayComplete);
          });
        });

        // Keyboard: Enter/Space opens modal
        item.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            item.click();
          }
        });
      }
    });

    container.appendChild(group);
  });
}

// ── Toggle logic (shared by checkbox + modal button) ─────────
function _toggleActivity(act, wasDone, store, dayActs, isToday, process, onDayComplete) {
  if (wasDone) {
    store.uncompleteActivity(act.id, process.id);
    toast(`↩️ ${act.action} desmarcado`);
  } else {
    store.completeActivity(act.id, process.id);
    toast(`✅ ${act.action} ¡listo!`);
  }

  const fresh = store.load();
  const freshProcess = fresh.processes?.find(p => p.id === process.id);
  if (freshProcess) {
    renderActivities(freshProcess, store, onDayComplete);
  }

  // Day complete check
  if (!wasDone && onDayComplete && isToday) {
    const totalDays = PROCESSES[process.type]?.totalDays ?? 7;
    const allDone   = dayActs.every(a => store.isCompleted(a.id, process.id));
    if (allDone) onDayComplete(process.currentDay, totalDays);
  }
}
